import React, { useState, useEffect } from 'react';
import s from './s.module.less';
import { Button } from 'antd';
import SignatureModal from './SignatureModal';
import { getDefaultProviderSignature, getProviderPrivateFileSigned } from 'api/common';
import { IProviderSignature, ESignatureType } from 'types/treatment';

interface ProviderSignatureProps {
    onSignatureStatusChange?: (hasSignature: boolean) => void;
}

const ProviderSignature: React.FC<ProviderSignatureProps> = ({ onSignatureStatusChange }) => {
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [defaultSignature, setDefaultSignature] = useState<IProviderSignature | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);

    const fetchSignature = async () => {
        const res = await getDefaultProviderSignature();
        if (!res.error && res.data?.data) {
            const sig = res.data.data;
            setDefaultSignature(sig);
            onSignatureStatusChange?.(!!sig.signatureFileKey);
            if (sig.signatureFileKey) {
                const previewRes = await getProviderPrivateFileSigned(sig.signatureFileKey);
                if (!previewRes.error && previewRes.data?.data?.singedUrl) {
                    setPreviewUrl(previewRes.data.data.singedUrl);
                }
            }
        } else {
            onSignatureStatusChange?.(false);
        }
    };

    useEffect(() => {
        fetchSignature();
    }, []);

    const handleModalClose = () => {
        setShowSignatureModal(false);
        // Refresh signature after modal closes (may have been created/updated)
        fetchSignature();
    };

    const renderPreview = () => {
        if (!defaultSignature?.signatureFileKey) {
            return (
                <div className={s.signaturePreviewBox} onClick={() => setShowSignatureModal(true)}>
                    <div className="w-8 h-8 flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                            <path
                                d="M20 15V20M20 20V25M20 20H25M20 20H15M35 20C35 28.2843 28.2843 35 20 35C11.7157 35 5 28.2843 5 20C5 11.7157 11.7157 5 20 5C28.2843 5 35 11.7157 35 20Z"
                                stroke="#0E62F4"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </div>
                    <p className="font-medium text-[#0e62f4] text-sm m-0">Add your signature</p>
                </div>
            );
        }

        return (
            <div
                className={s.signaturePreviewBox}
                onClick={() => setShowSignatureModal(true)}
                style={{ border: 'none', borderRadius: '8px' }}
            >
                {previewUrl ? (
                    <img
                        src={previewUrl}
                        alt="Signature"
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                ) : (
                    <p className="text-gray-400 text-sm m-0">Loading...</p>
                )}
            </div>
        );
    };

    return (
        <div className="flex flex-col gap-2 items-start w-full mt-6">
            <div className="flex items-center justify-between w-full">
                <p className="font-medium text-gray-700 text-sm m-0">Signature preview</p>
                {defaultSignature && (
                    <Button
                        type="link"
                        className="flex items-center gap-1"
                        onClick={() => setShowSignatureModal(true)}
                        icon={
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                            >
                                <path
                                    d="M12.2618 1.23816L12.7921 0.707825V0.707825L12.2618 1.23816ZM3.08333 12.7737V13.5237C3.28225 13.5237 3.47301 13.4446 3.61366 13.304L3.08333 12.7737ZM0.75 12.7737H0C0 13.1879 0.335786 13.5237 0.75 13.5237L0.75 12.7737ZM0.75 10.3929L0.21967 9.86261C0.0790176 10.0033 0 10.194 0 10.3929H0.75ZM9.90478 1.23816L10.4351 1.76849C10.7931 1.4105 11.3735 1.4105 11.7315 1.76849L12.2618 1.23816L12.7921 0.707825C11.8484 -0.235942 10.3182 -0.235942 9.37445 0.707825L9.90478 1.23816ZM12.2618 1.23816L11.7315 1.76849C12.0895 2.12647 12.0895 2.70687 11.7315 3.06485L12.2618 3.59518L12.7921 4.12551C13.7359 3.18174 13.7359 1.65159 12.7921 0.707825L12.2618 1.23816ZM12.2618 3.59518L11.7315 3.06485L2.553 12.2433L3.08333 12.7737L3.61366 13.304L12.7921 4.12551L12.2618 3.59518ZM3.08333 12.7737V12.0237H0.75V12.7737V13.5237H3.08333V12.7737ZM9.90478 1.23816L9.37445 0.707825L0.21967 9.86261L0.75 10.3929L1.28033 10.9233L10.4351 1.76849L9.90478 1.23816ZM0.75 10.3929H0V12.7737H0.75H1.5V10.3929H0.75ZM8.90478 2.23816L8.37445 2.76849L10.7315 5.12551L11.2618 4.59518L11.7921 4.06485L9.43511 1.70783L8.90478 2.23816Z"
                                    fill="currentColor"
                                />
                            </svg>
                        }
                    >
                        Edit
                    </Button>
                )}
            </div>
            {renderPreview()}

            <SignatureModal
                open={showSignatureModal}
                onCancel={handleModalClose}
                onConfirm={handleModalClose}
                initialData={defaultSignature || undefined}
            />
        </div>
    );
};

export default ProviderSignature;
